import { useMemo } from "react"
import { BackendName } from '../types'
import { validateBackend } from '../utils'
import { useFirebaseBackend, useLocalStorageBackend, useMemoryBackend } from './index'

const DEFAULT_BACKENDS: BackendName[] = ['firebase', 'localStorage', 'memory']

// New type for backend config
export type BackendConfig = BackendName | { name: BackendName, [key: string]: any }

function getBackendConfig(backendConfigs: BackendConfig[], name: BackendName) {
  const found = backendConfigs.find(
    b => b && ((typeof b === 'string' && b === name) || (typeof b === 'object' && b.name === name))
  );
  return typeof found === 'object' ? (({ name, ...rest }) => rest)(found) : {};
}

/**
 * Hook for selecting and managing a single active backend instance
 * Always calls all possible backend hooks at the top level to follow Rules of Hooks,
 * then selects the first available backend from the provided list
 */
const useClipboardBackend = (
  backendConfigs: BackendConfig[] = []
) => {
  // Always call all possible backend hooks at the top level to follow Rules of Hooks
  const firebaseBackend = useFirebaseBackend(getBackendConfig(backendConfigs, 'firebase'))
  const localStorageBackend = useLocalStorageBackend(getBackendConfig(backendConfigs, 'localStorage'))
  const memoryBackend = useMemoryBackend(getBackendConfig(backendConfigs, 'memory'))

  // Map backend instances to their names for lookup
  const backendInstances = useMemo(() => ({
    firebase: firebaseBackend,
    localStorage: localStorageBackend,
    memory: memoryBackend
  }), [firebaseBackend, localStorageBackend, memoryBackend])

  // Determine which backends to use
  const backendsToTry = useMemo(() => {
    if (!backendConfigs || backendConfigs.length === 0) return DEFAULT_BACKENDS.map(name => ({ name }))
    return backendConfigs.map(b => typeof b === 'string' ? { name: b } : b)
  }, [backendConfigs])

  // Select the first available backend
  const activeBackend = useMemo(() => {
    for (let i = 0; i < backendsToTry.length; i++) {
      const { name: backendName } = backendsToTry[i]
      const backend = backendInstances[backendName]

      if (!backend) {
        console.warn(`[Clipboard] Unknown backend name: ${backendName}`)
        continue
      }

      if (validateBackend(backend) && backend.isAvailable()) {
        return backend
      }
    }

    return null
  }, [backendsToTry, backendInstances])

  return activeBackend
}

export default useClipboardBackend
